import {
  FacebookFilled,
  GlobalOutlined,
  GoogleOutlined,
  InstagramOutlined,
  LinkedinFilled,
  PinterestFilled,
  TikTokOutlined,
  XOutlined,
} from "@ant-design/icons";
import { useMemo } from "react";
import type { IntegrationType } from "store/user/userConstants";

// Re-define types needed within the hook (or import from PostPreview if preferred)
const SUPPORTED_SOCIAL_CARD_KEYS = [
  "facebook",
  "instagram",
  "linkedIn",
  "twitter",
  "pinterest",
  "tiktok",
  "linkedInProfile",
  "googleBusiness",
  "instagramStory",
] as const;

type SupportedSocialCardKey = (typeof SUPPORTED_SOCIAL_CARD_KEYS)[number];
type PreviewType = SupportedSocialCardKey | "Generic";

// Hook Input Props Type
type UsePreviewSelectorOptionsProps = {
  publishTo: IntegrationType[];
};

// Hook Return Type
type UsePreviewSelectorOptionsReturn = {
  options: { value: PreviewType; label: React.ReactNode }[];
  showInstagramSubSelector: boolean;
};

export const usePreviewSelectorOptions = ({
  publishTo,
}: UsePreviewSelectorOptionsProps): UsePreviewSelectorOptionsReturn => {
  const { options, showInstagramSubSelector } = useMemo(() => {
    // --- Determine Base Set of Types --- //
    let baseIntegrationTypes: SupportedSocialCardKey[];

    baseIntegrationTypes = publishTo.filter(
      (type): type is SupportedSocialCardKey =>
        SUPPORTED_SOCIAL_CARD_KEYS.includes(type as SupportedSocialCardKey)
    );

    const integrationSet = new Set(baseIntegrationTypes);

    // --- Determine Sub-selector Visibility (based on CURRENT selection) --- //
    const showSubSelector =
      integrationSet.has("instagram") && integrationSet.has("instagramStory");
    // --- End Sub-selector Logic --- //

    // --- Deduplicate and Finalize Options (based on determined base set) --- //

    // Consolidate LinkedIn types: If 'linkedInProfile' exists, ensure 'linkedIn' is used for the selector value
    // and remove 'linkedInProfile' from the set to avoid duplicates.
    if (integrationSet.has("linkedInProfile")) {
      integrationSet.delete("linkedInProfile");
      integrationSet.add("linkedIn"); // Ensure 'linkedIn' is present if profile exists
    }

    // De-duplicate Instagram in the final set if sub-selector is active
    if (
      showSubSelector &&
      integrationSet.has("instagram") &&
      integrationSet.has("instagramStory")
    ) {
      integrationSet.delete("instagramStory");
    }
    // If sub-selector is NOT shown, but both are somehow selected/present, prioritize post
    else if (
      !showSubSelector &&
      integrationSet.has("instagram") &&
      integrationSet.has("instagramStory")
    ) {
      integrationSet.delete("instagramStory");
    }

    const uniqueIntegrationTypes = Array.from(integrationSet);

    const getIcon = (type: PreviewType) => {
      switch (type) {
        case "facebook":
          return <FacebookFilled />;
        case "instagram":
        case "instagramStory":
          return <InstagramOutlined />;
        case "linkedIn": // Only one LinkedIn icon needed now
          return <LinkedinFilled />;
        case "twitter":
          return <XOutlined />;
        case "pinterest":
          return <PinterestFilled />;
        case "tiktok":
          return <TikTokOutlined />;
        case "googleBusiness":
          return <GoogleOutlined />;
        case "Generic":
        default:
          return <GlobalOutlined />;
      }
    };

    const options: { value: PreviewType; label: React.ReactNode }[] = [
      {
        value: "Generic",
        label: getIcon("Generic"),
      },
      ...uniqueIntegrationTypes.map((type) => ({
        value: type,
        label: getIcon(type),
      })),
    ];

    return { options, showInstagramSubSelector: showSubSelector };
  }, [publishTo]);

  return { options, showInstagramSubSelector };
};
